import React from "react";
import { Link } from "react-router-dom";
import { Shield, AlertTriangle, FileText, CheckCircle2, BadgeCheck } from "lucide-react";

// Safety & insurance — public marketing-style content. No state, just a hero +
// 3 sections (coverage, what's required, what to do in an incident).
export default function Safety() {
  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-16" data-testid="safety-page">
      <div className="eyebrow mb-3">Trust & safety</div>
      <h1 className="zc-headline text-4xl sm:text-5xl md:text-6xl tracking-tight">
        Drive covered. <span className="text-[var(--zc-accent)]">Always.</span>
      </h1>
      <p className="text-[var(--zc-text-2)] mt-4 max-w-2xl text-base">
        Every trip on Z-Cars is protected end-to-end. From identity-verified renters
        to per-trip insurance, we've removed the guesswork of peer-to-peer car sharing.
      </p>

      {/* Coverage grid */}
      <div className="grid md:grid-cols-2 gap-4 mt-12">
        <CoverCard
          icon={<Shield size={22} />}
          tone="green"
          title="Third-party liability coverage"
          body="Auto-attached to every confirmed booking — covers bodily injury and property damage to others. Coverage limits vary by region; contact support for the details that apply to your trip."
        />
        <CoverCard
          icon={<BadgeCheck size={22} />}
          tone="blue"
          title="Collision damage waiver"
          body="Damage to the rented vehicle is covered up to fair market value. Deductibles vary by region — contact support for coverage details before your trip."
        />
      </div>

      {/* Verified renters/hosts */}
      <section className="mt-20">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="eyebrow mb-3">Identity verification</div>
            <h2 className="zc-headline text-3xl md:text-4xl">
              Every renter and host is verified.
            </h2>
            <p className="text-[var(--zc-text-2)] mt-4 leading-relaxed">
              Before you can book or list, we run a real-time check pairing a government
              ID with a live selfie. Our system rejects spoofs (photos of photos, screen
              replays, masks) and flags anomalies for human review within 2 business hours.
            </p>
          </div>
          <ul className="space-y-3">
            {[
              "Valid driver's licence match (front, back, and live selfie)",
              "Live selfie with passive liveness detection",
              "Driving licence validity check (renters only)",
              "Sanctions and watchlist screening",
              "ID images encrypted at rest, purged 90 days post-trip",
            ].map((line) => (
              <li key={line} className="flex items-start gap-3 text-sm text-[var(--zc-text)]">
                <CheckCircle2 size={16} className="text-[var(--zc-success)] mt-0.5 shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Incident steps */}
      <section className="mt-20">
        <div className="eyebrow mb-3">If something goes wrong</div>
        <h2 className="zc-headline text-3xl md:text-4xl">In an incident, follow these steps.</h2>
        <div className="grid md:grid-cols-4 gap-4 mt-8">
          {[
            { n: "01", t: "Make sure everyone is safe", b: "Move to a safe spot. If anyone is injured, call local emergency services immediately." },
            { n: "02", t: "Document the scene", b: "Take photos of the vehicles, license plates, the location, and any visible damage." },
            { n: "03", t: "Exchange info", b: "Names, contact details, insurance, and license plates of all parties involved." },
            { n: "04", t: "Report in-app", b: "Open the active trip → Report incident. We'll guide the claim from there." },
          ].map((s) => (
            <div key={s.n} className="zc-card p-5">
              <div className="text-xs font-mono text-[var(--zc-accent)] font-bold">{s.n}</div>
              <div className="zc-title text-lg mt-2">{s.t}</div>
              <p className="text-sm text-[var(--zc-text-2)] mt-2 leading-relaxed">{s.b}</p>
            </div>
          ))}
        </div>
        <div className="zc-card p-5 mt-6 flex items-start gap-4" style={{ background: "rgba(245,158,11,0.06)", borderColor: "rgba(245,158,11,0.3)" }}>
          <AlertTriangle size={20} className="text-[var(--zc-warn)] shrink-0 mt-0.5" />
          <div className="text-sm text-[var(--zc-text)]">
            <strong>Important:</strong> Failing to report an incident within 24 hours may void coverage. When in doubt, report — even minor incidents.
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="mt-20">
        <div className="eyebrow mb-3">Eligibility</div>
        <h2 className="zc-headline text-3xl md:text-4xl">Who can rent on Z-Cars.</h2>
        <ul className="grid md:grid-cols-2 gap-3 mt-8">
          {[
            "Minimum age: 18 (enforced at booking after ID verification)",
            "Valid driver's license held for at least 12 months",
            "No DUI / reckless driving in the past 3 years",
            "Maximum 2 moving violations in the past 3 years",
            "Valid payment method on file",
            "Successful identity verification",
          ].map((line) => (
            <li key={line} className="flex items-start gap-3 text-sm text-[var(--zc-text)]">
              <FileText size={14} className="text-[var(--zc-text-3)] mt-1 shrink-0" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </section>

      <div className="mt-16 zc-card p-8 text-center">
        <h3 className="zc-headline text-2xl">Still have questions?</h3>
        <p className="text-sm text-[var(--zc-text-2)] mt-2 max-w-md mx-auto">
          Our help center covers the most common scenarios end-to-end.
        </p>
        <Link to="/help" className="inline-flex items-center px-6 h-11 zc-btn-primary text-sm mt-5" data-testid="safety-go-help">
          Visit help center
        </Link>
      </div>
    </div>
  );
}

function CoverCard({ icon, title, body, tone }) {
  const palettes = {
    green: { bg: "rgba(16,185,129,0.10)", fg: "var(--zc-success)" },
    blue: { bg: "var(--zc-secondary-soft)", fg: "var(--zc-secondary)" },
    accent: { bg: "var(--zc-accent-soft)", fg: "var(--zc-accent)" },
  };
  const p = palettes[tone] || palettes.blue;
  return (
    <div className="zc-card p-6">
      <div className="w-11 h-11 grid place-items-center rounded-lg" style={{ background: p.bg, color: p.fg }}>
        {icon}
      </div>
      <div className="zc-title text-xl mt-5">{title}</div>
      <p className="text-sm text-[var(--zc-text-2)] mt-3 leading-relaxed">{body}</p>
    </div>
  );
}
